import { Avatar } from '@dtx-company/design-system/src/components/Avatar/Avatar'
import { Box } from '@dtx-company/design-system/src/components/Box/Box'
import { Button } from '@dtx-company/design-system/src/components/Button/Button'
import { CircularProgress } from '@dtx-company/design-system/src/components/CircularProgress/CircularProgress'
import {
  Dialog,
  DialogContent,
  IconButton,
  ThemeProvider,
  capitalize,
  useMediaQuery
} from '@mui/material'
import { EntityTypes } from '@dtx-company/inter-app/src/redux/slices/modal'
import { FileUploadInput } from '@dtx-company/shared-components/src/components'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Typography } from '@dtx-company/design-system/src/components/Typography/Typography'
import { theme } from '@dtx-company/design-system/src/theme'
import { useProfilePictureModalState } from './useProfilePictureModalState'
import EditIcon from '@mui/icons-material/Edit'
import React from 'react'

export const ProfilePictureModal: React.FC = () => {
  const {
    modalOpen,
    setClose,
    profileImage,
    handleSaveProfileImage,
    handleDeleteProfileImage,
    imageFileString,
    error,
    entityType,
    fullName,
    submitting,
    handleUpload,
    pictureType
  } = useProfilePictureModalState()
  const hasImage = Boolean(imageFileString || profileImage)
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`)
  return (
    <ThemeProvider theme={theme}>
      <Dialog open={modalOpen} onClose={setClose} maxWidth={isMobile ? undefined : 'md'}>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              maxWidth: 550,
              minHeight: 500,
              flexDirection: 'column'
            }}
          >
            <Typography align="left" variant="subtitleMedium">
              {`${capitalize(pictureType)} Picture`}
            </Typography>
            <Typography>
              {entityType === EntityTypes.USER
                ? "Upload a profile picture to let other people recognize you and let you know when you're signed in to your account."
                : 'Upload a team photo to easily recognize your team. An admin can change this photo at any time.'}
            </Typography>
            <Box sx={{ margin: 'auto', position: 'relative' }}>
              <Avatar size="xxLarge" alt={fullName} src={imageFileString || profileImage} />
              {hasImage && !submitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 16
                  }}
                >
                  <FileUploadInput handleUpload={handleUpload} id="profile-picture-upload">
                    <IconButton
                      sx={theme => ({
                        boxShadow: theme.shadows[1],
                        backgroundColor: theme.palette.primary.contrastText,
                        ':hover': {
                          backgroundColor: theme.palette.background.default
                        }
                      })}
                      component="span"
                    >
                      <EditIcon />
                    </IconButton>
                  </FileUploadInput>
                </Box>
              )}
              {submitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 16
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
            {hasImage ? (
              <>
                <Button
                  onClick={async () => await handleSaveProfileImage()}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  disabled={submitting || Boolean(profileImage && !imageFileString)}
                >
                  {`Save ${pictureType} picture`}
                </Button>
                <Spacer mb="8px" />
                <Button
                  onClick={async () => await handleDeleteProfileImage()}
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={submitting}
                >
                  {`Delete ${pictureType} picture`}
                </Button>
              </>
            ) : (
              <FileUploadInput handleUpload={handleUpload} id="profile-picture-upload">
                <Button component="span" fullWidth variant="outlined" color="secondary">
                  {`Upload a ${pictureType} picture`}
                </Button>
              </FileUploadInput>
            )}
            {error && <Typography color="error">{error}</Typography>}
          </Box>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  )
}
